// Cache for the data to avoid multiple fetches
let cachedData = null;

// First check if we have a company in sessionStorage
let storedCompanyId = null;
try {
  const storedCompany = sessionStorage.getItem("selectedCompany");
  if (storedCompany) {
    const company = JSON.parse(storedCompany);
    storedCompanyId = company.id;
  }
} catch (error) {
  console.error("Error reading company from sessionStorage:", error);
}

// Enhanced fetch with basic auth
export const authenticatedFetch = async (url, options = {}) => {
  const basicAuth = localStorage.getItem('basicAuth');
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(basicAuth && { 'Authorization': `Basic ${basicAuth}` }),
      ...options.headers
    },
    ...options
  };

  const response = await fetch(url, defaultOptions);
  
  if (response.status === 401) {
    localStorage.removeItem('basicAuth');
    window.location.reload(); // Force re-authentication
  }
  
  return response;
};

const simulateApiDelay = (ms = 800) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const CONFIG = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || "https://ownli-fd.azurewebsites.net" || "http://localhost:8080",
  companyId:
    process.env.REACT_APP_COMPANY_ID || "3a021213c347c887857f5b5ee5d86af8",
  region: process.env.REACT_APP_REGION || "India",
  currencyCode: process.env.REACT_APP_CURRENCY_CODE || "INR",
  taxType: process.env.REACT_APP_TAX_TYPE || "GST",
};

export default CONFIG;

/**
 * Fetches policies from the server with pagination
 */
export async function fetchPolicyList(params = {}) {
  return [];
}

// Function to fetch the complete data set (only once)
// Function to fetch the complete data set from the server
async function fetchCompleteData(invoiceId) {
  if (cachedData && cachedData.invoiceId === invoiceId) {
    return cachedData;
  }

  try {
    let storedCompanyId = null;
    try {
      const storedCompany = sessionStorage.getItem("selectedCompany");
      if (storedCompany) {
        const company = JSON.parse(storedCompany);
        storedCompanyId = company.id;
      }
    } catch (error) {
      console.error("Error reading company from sessionStorage:", error);
    }
    let companyId = storedCompanyId || CONFIG.companyId;

    const url = `${CONFIG.baseUrl}/invoice/match/output/${companyId}?invoice_id=${invoiceId}`;
    console.log(`Fetching data from ${url}`);

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // First get the response as text
    const textResponse = await response.text();

    // Then parse the text as JSON
    let data;
    try {
      data = JSON.parse(textResponse);
      if (data.content) {
        data = JSON.parse(data.content);
      }
    } catch (jsonError) {
      console.error("Error parsing JSON response:", jsonError);
      console.log("Raw response:", textResponse);
      throw new Error("Failed to parse server response as JSON");
    }

    // Add the invoiceId to cachedData for reference
    data.invoiceId = invoiceId;
    cachedData = data;
    return data;
  } catch (error) {
    console.error("Error fetching data from server:", error);
    throw error;
  }
}

// API functions
export async function fetchInvoice(invoiceId) {
  try {
    const completeData = await fetchCompleteData(invoiceId);
    return completeData.invoice;
  } catch (error) {
    console.error("Error fetching invoice:", error);
    throw error;
  }
}

export async function fetchPO(invoiceId) {
  try {
    const completeData = await fetchCompleteData(invoiceId);
    return completeData.po;
  } catch (error) {
    console.error("Error fetching PO:", error);
    throw error;
  }
}

export async function fetchMatchResults(invoiceId) {
  try {
    const completeData = await fetchCompleteData(invoiceId);
    let matchData = completeData.output;
    if (!matchData) {
      matchData = completeData.invoice;
    }
    console.log(matchData);
    // Process match data to create the expected format for the UI
    const matchResults = {
      matchedItems: [],
      missingItems: [],
      unexpectedItems: [],
      invoice: matchData,
      summary: {
        total: 0,
        matched: 0,
        missing: 0,
        unexpected: 0,
      },
    };

    // Get reference to the invoice data (for adding match status)
    const invoiceData = completeData.invoice;

    // Process items based on match status
    matchData.items.forEach((item) => {
      if (!item.match_status)
        item.match_status = {
          status: "ERROR",
          description: "Item not matched",
        };
      item.item_id = item.item_id || item.sequence;
      if (!item.match_status) return;

      const status = item.match_status?.status;
      if (status === "SUCCESS") {
        matchResults.matchedItems.push({
          status: "matched",
          invoiceLineId: item.item_id,
          poLineId: null, // We don't have direct mapping here
          confidence: item.match_status.confidence,
          differences: {},
          // Add the full item data for reference
          invoiceLine: item,
        });
        matchResults.summary.matched++;
      } else if (status === "WARNING") {
        matchResults.matchedItems.push({
          status: "matched",
          invoiceLineId: item.item_id,
          poLineId: null,
          confidence: item.match_status.confidence,
          differences: {},
          invoiceLine: item,
        });
        matchResults.summary.matched++;
      } else if (status === "ERROR" || status === "UNKNOWN") {
        // Check if it's a missing or unexpected item
        //if (item.match_status.description === "Item not matched") {
        matchResults.unexpectedItems.push({
          status: "unexpected",
          invoiceLineId: item.item_id,
          invoiceLine: item,
          similarItems: [], // We don't have similar items in this data
        });
        matchResults.summary.unexpected++;
        //}
      }
    });

    // Create missing items by finding PO items that don't match any invoice items
    // This is a simplified approach - in real implementation, this would be determined by the matching algorithm
    const poData = completeData.po;

    // // Create a set of all matched invoice item sequences
    // const matchedInvoiceSequences = new Set(
    //   matchResults.matchedItems.map((item) =>
    //     item.invoiceLine ? item.invoiceLine.sequence : -1
    //   )
    // );

    // Find PO items that don't match any invoice item by sequence
    poData &&
      poData.items.forEach((poItem, index) => {
        // Only consider the first few items to avoid too many missing items in the UI
        if (index > 5 && index < 10) {
          matchResults.missingItems.push({
            status: "missing",
            poLineId: poItem.item_id,
            poLine: poItem,
          });
          matchResults.summary.missing++;
        }
      });

    matchResults.summary.total =
      matchResults.summary.matched +
      matchResults.summary.missing +
      matchResults.summary.unexpected;

    // Add the match statuses to the result
    matchResults.match_statuses = matchData.match_statuses;

    return matchResults;
  } catch (error) {
    console.error("Error fetching match results:", error);
    throw error;
  }
}

// Function to fetch the complete data set (only once)
// Function to fetch the complete data set from the server
export async function triggerMatch(invoiceId) {
  try {
    let storedCompanyId = null;
    try {
      const storedCompany = sessionStorage.getItem("selectedCompany");
      if (storedCompany) {
        const company = JSON.parse(storedCompany);
        storedCompanyId = company.id;
      }
    } catch (error) {
      console.error("Error reading company from sessionStorage:", error);
    }
    let companyId = storedCompanyId || CONFIG.companyId;
    const formData = new FormData();
    formData.append("invoice_id", invoiceId);
    formData.append("request_id", "");

    await fetch(
      `${CONFIG.baseUrl}/invoice/match/${companyId}?invoice_id=${invoiceId}&request_id=`,
      {
        method: "POST",
        body: formData,
        // No need to set Content-Type header as browser will set it with boundary for FormData
      }
    );
  } catch (error) {
    console.error("Error fetching data from server:", error);
    throw error;
  }
}

export async function updateMatchStatus(itemId, newStatus, reason) {
  await new Promise((resolve) => setTimeout(resolve, 300));
  // In a real implementation, this would update the status on the server
  return { success: true, message: "Status updated successfully" };
}

export async function updateLineItem(lineItemId, updates) {
  await new Promise((resolve) => setTimeout(resolve, 400));
  // In a real implementation, this would send the updates to the server
  return { success: true, message: "Line item updated successfully" };
}
